/**
 * Margem dos créditos de IA — quanto custa, de verdade, cada crédito que a tarifa
 * cobra (docs/PLANO_CREDITOS_IA.md §5).
 *
 * A referência é a taxa de lançamento: 1 crédito ≈ R$ 0,10 de custo. Acima dela a
 * tarifa está barata para o que o modelo gasta; abaixo, sobra margem (é o que um
 * modelo mais barato rende). Preparo e Secretário não debitam créditos: aparecem
 * só com o custo, sem razão. Só entram as linhas tarifadas — o histórico convertido
 * do custo na migração mediria a conversão, não a tarifa.
 */
import { useQuery } from '@tanstack/react-query';

import { fetchMargemIa } from '../../api/adminIa';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Spinner } from '../../components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { classifyError } from '../../lib/errors';
import { cn } from '../../lib/utils';

/** Custo de lançamento de 1 crédito, em centavos (backend: CENTAVOS_POR_CREDITO_LEGADO). */
const REFERENCIA_CENTAVOS = 10;

const ROTULO_PRODUTO: Record<string, string> = {
  CHAT_PROCESSO: 'Chat de IA',
  EDITOR_REVISAO: 'Revisão do editor',
  PREPARO: 'Preparo dos autos',
  SECRETARIO: 'Secretário', // pragma: allowlist secret
};

const ROTULO_NIVEL: Record<string, string> = {
  DIRETA: 'Direta',
  PADRAO: 'Padrão',
  APROFUNDADA: 'Aprofundada',
};

function formatarReais(centavos: number, casas = 2): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function formatarMes(mes: string): string {
  const [ano, m] = mes.split('-');
  return `${m}/${ano}`;
}

export function MargemIaCard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'ia', 'margem'],
    queryFn: () => fetchMargemIa(3),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Margem dos créditos</CardTitle>
        <p className="text-sm text-muted-foreground">
          Custo real de 1 crédito nos últimos 3 meses. A referência de lançamento é{' '}
          {formatarReais(REFERENCIA_CENTAVOS)}: acima dela, a tarifa está abaixo do custo.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {error ? (
          <Alert variant="destructive" className="m-4 w-auto">
            <AlertDescription>{classifyError(error).message}</AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size={32} label="Carregando a margem" />
          </div>
        ) : !data?.length ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">Nenhum uso de IA nos últimos 3 meses.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mês</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Nível</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                  <TableHead className="text-right">Créditos</TableHead>
                  <TableHead className="text-right">Custo</TableHead>
                  <TableHead className="text-right">Custo por crédito</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((linha) => {
                  const porCredito = linha.custo_por_credito_centavos;
                  return (
                    <TableRow key={`${linha.mes}-${linha.produto}-${linha.nivel ?? ''}`}>
                      <TableCell className="tabular-nums">{formatarMes(linha.mes)}</TableCell>
                      <TableCell>{ROTULO_PRODUTO[linha.produto] ?? linha.produto}</TableCell>
                      <TableCell>{linha.nivel ? (ROTULO_NIVEL[linha.nivel] ?? linha.nivel) : '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{linha.acoes.toLocaleString('pt-BR')}</TableCell>
                      <TableCell className="text-right tabular-nums">{linha.creditos.toLocaleString('pt-BR')}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatarReais(linha.custo_centavos)}</TableCell>
                      <TableCell
                        className={cn(
                          'text-right tabular-nums',
                          porCredito !== null && porCredito > REFERENCIA_CENTAVOS && 'font-medium text-destructive',
                        )}
                      >
                        {/* 3 casas: com 2, R$ 0,104 (acima da referência, em vermelho) sairia
                            igual aos R$ 0,10 dela. */}
                        {porCredito === null ? '—' : formatarReais(porCredito, 3)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
